export const HOME_CONTENT = {
  tagline: 'Build. Engineer. Ship with AI.',
  heroIntro:
    'Learn modern software engineering, practice with hands-on Labs, build real-world projects, and grow toward professional opportunities — independently or through guided learning with IDC Academy.',
  training: {
    title: 'Don’t just learn technology. Become an AI-native software engineer.',
    intro:
      'Build real-world applications, learn from industry mentors, master modern engineering practices, and become ready to design, build, and ship production-grade software with AI.',
    pointsEyebrow: 'What you gain beyond technical skills',
    points: [
      {
        title: 'Sharper skills',
        description:
          'Move beyond syntax into APIs, cloud, DevOps foundations, and AI-assisted engineering so you can build and explain real systems.',
      },
      {
        title: 'Stronger capabilities',
        description:
          'Practice architecture discussions, code reviews, and production thinking — the capabilities that separate job-ready engineers from course-completers.',
      },
      {
        title: 'Build a career that compounds',
        description:
          'Build the engineering depth for modern software roles — backed by a portfolio that demonstrates how you design, build, and ship real software.',
        roles: ['Backend', 'Full Stack', 'Cloud', 'AI Engineering'],
      },
    ],
  },
  ctas: {
    exploreProgram: 'Explore the Program',
    exploreAcademyPrograms: 'Explore IDC Academy',
    browseSelfPacedCourses: 'Browse Courses',
    startJourney: 'Start your AI engineering journey',
    bookInterview: 'Book a Technical Interview',
  },
  twoPaths: {
    eyebrow: 'Choose how you want to learn',
    title: 'One platform. Two clear paths.',
    intro: 'Learn independently with Courses and Labs, or join a mentor-led IDC Academy program.',
    academyTitle: 'Mentor-led engineering programs',
    academyBody: 'A structured cohort with curriculum, reviews, and interview practice.',
    academyPoints: [
      'Students and career switchers',
      'Learners who want accountability',
      'Developers who want mentor-reviewed portfolios',
    ],
    coursesTitle: 'Learn → Practice → Build',
    coursesBody:
      'Learn through structured topics and hands-on Labs, with optional professional reviews and interview practice sessions available at enrollment.',
  },
  projects: {
    eyebrow: 'What will you build?',
    title: 'Learn by building real systems',
    intro:
      'You leave with evidence — applications, architecture, and shipping practice — not a certificate that only proves you watched videos.',
    items: [
      {
        title: 'Enterprise Business API',
        stack: ['.NET Web API / Spring Boot / Node API', 'Python', 'FastAPI'],
        description:
          'Backend engineering, REST APIs, security, testing, database design, caching, AI-assisted development, and production practices.',
      },
      {
        title: 'Cloud-Native Microservices Platform',
        stack: ['.NET / Java / Node', 'Python', 'FastAPI', 'Docker', 'CI/CD'],
        description:
          'Distributed systems, service communication, asynchronous processing, cloud deployment, automated delivery, and observability.',
      },
      {
        title: 'AI-Powered Enterprise Assistant',
        stack: ['.NET / Java / Node', 'Python', 'FastAPI', 'LLMs', 'RAG'],
        description:
          'Enterprise AI, retrieval, tool calling, AI workflows, evaluation, guardrails, and AI application architecture.',
      },
    ],
  },
  method: {
    eyebrow: 'How you grow as an engineer',
    title: 'From learning to engineering',
    promise: 'Learn → Build → Integrate → Scale → Automate → Engineer with AI → Operate → Prove',
    steps: [
      {
        title: 'Learn',
        description: 'Build strong software engineering and backend fundamentals.',
      },
      {
        title: 'Build',
        description: 'Develop applications using .NET Web API, Spring Boot, or Node API — with Python and FastAPI on every track.',
      },
      {
        title: 'Integrate',
        description: 'Connect APIs, databases, services, and cloud components.',
      },
      {
        title: 'Scale',
        description: 'Apply microservices and system design principles.',
      },
      {
        title: 'Automate',
        description: 'Implement Docker, Git, CI/CD, and automated deployments.',
      },
      {
        title: 'Engineer with AI',
        description: 'Use LLMs, RAG, agents, tools, and AI-assisted development.',
      },
      {
        title: 'Operate',
        description: 'Apply security, observability, and production engineering practices.',
      },
      {
        title: 'Prove',
        description: 'Complete projects, architecture reviews, technical assessments, and interviews.',
      },
    ],
  },
  program: {
    eyebrow: 'What you will learn',
    title: 'What you will learn, build, and leave with',
    intro:
      'A structured path from fundamentals to production-ready engineering — with mentoring, projects, and a portfolio that shows how you work.',
    durationFact: '6 Months',
    pillars: [
      {
        title: 'Duration',
        factKey: 'duration',
        description: 'A cohort-based program designed around real delivery, not lecture hours.',
      },
      {
        title: 'Projects',
        factKey: 'projects',
        description: 'Ship systems you can explain: architecture, code, reviews, and production thinking.',
      },
      {
        title: 'Mentoring',
        fact: 'Mentor-led Reviews',
        description:
          'Ongoing mentor-led reviews throughout the program — on how you design, build, and communicate, not only whether the code runs.',
      },
      {
        title: 'Interview',
        fact: 'Technical Mock Interviews',
        description: 'A realistic practice Interview session with an industry expert so you can see the gaps and get direct feedback.',
      },
      {
        title: 'Portfolio',
        fact: 'GitHub + Project Portfolio',
        description: 'Leave with evidence of how you design, build, and ship — not only a certificate.',
      },
      {
        title: 'What you leave with',
        description:
          'A portfolio of real systems, assessment evidence, interview feedback, and the engineering practices needed to design, build, and ship with AI.',
      },
    ],
  },
  audience: {
    eyebrow: 'Who is this for?',
    title: 'Built for people who want to become engineers, not course-completers',
    items: [
      {
        title: 'Students',
        description: 'Turn programming basics into systems you can build, explain, and show.',
      },
      {
        title: 'Freshers',
        description: 'Go beyond syntax and tutorials into APIs, cloud, reviews, and production practice.',
      },
      {
        title: 'Early-career engineers',
        description: 'Deepen architecture, AI-assisted engineering, and the portfolio that compounds your next role.',
      },
    ],
  },
  why: {
    eyebrow: 'Why learn from us?',
    title: 'Industry experience, mentoring, and an engineering focus',
    items: [
      {
        title: 'Industry mentors',
        description: 'Learn from practitioners who ship software — through reviews, architecture discussions, and feedback you can act on.',
      },
      {
        title: 'Engineering, not spectacle',
        description:
          'AI is part of the engineering workflow — not a collection of disconnected tools. Learn how to use AI to design, build, test, review, and ship software.',
      },
      {
        title: 'Evidence you can show',
        description:
          'Projects, code reviews, assessments, and interview feedback — so you know where you stand and what to learn next.',
      },
    ],
  },
  interview: {
    eyebrow: 'Technical interview',
    title: 'Assess your engineering skills',
    intro:
      'A realistic session with an industry expert so you can see the gaps, get direct feedback, and decide your next step with evidence.',
    facts: '60 minutes · Industry interviewer · Detailed feedback',
  },
  contribute: {
    title: 'Contribute. Mentor. Interview.',
    subtitle: 'Help the next generation of engineers grow.',
    intro:
      'Share your industry experience with developers through mentoring, technical interviews, architecture reviews, and practical feedback.',
    mentor: {
      title: 'Apply as a Mentor',
      description:
        'Guide learners through engineering decisions, project reviews, architecture discussions, and practical software development.',
      cta: 'Apply as a Mentor',
      path: '/mentor-registration',
    },
    interviewer: {
      title: 'Apply as an Interviewer',
      description:
        'Conduct realistic technical interviews, evaluate engineering skills, and provide actionable feedback to help developers improve.',
      cta: 'Apply as an Interviewer',
      path: '/interviewer-registration',
    },
  },
  finalCta: {
    title: 'Know where you stand. Know what to learn next.',
    intro:
      'Start a course, explore IDC Academy, or book a technical interview for expert feedback on your next step.',
  },
} as const;
